import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Languages } from 'lucide-react';
import type { SupportedLanguage } from '@kundaliai/shared';
import { useLanguageStore } from '@/stores/languageStore';
import { LOCALES, useLocale, type Locale } from '@/lib/i18n';
import { languageTarget } from '@/lib/languageTarget';
import { cn } from '@/lib/utils';

const APP_LANGUAGES: readonly string[] = ['en', 'hi', 'mr'] satisfies readonly SupportedLanguage[];
const SHORT: Record<Locale, string> = { en: 'EN', hi: 'हि', mr: 'म', ne: 'ने' };

/**
 * One language control for the whole site. Picking a language does two things:
 * navigates to this page in that language (or the nearest page that exists in
 * it — see languageTarget), and, where the chat supports the language, stores it
 * as the app preference so answers come back in it too. It used to do only the
 * second, which public pages no longer read, so it looked broken.
 */
export function LanguageToggle() {
  const locale = useLocale();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const choose = (code: Locale) => {
    setOpen(false);
    if (APP_LANGUAGES.includes(code)) setLanguage(code as SupportedLanguage);
    const to = languageTarget(pathname, code);
    if (to) navigate(to);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded-md transition-colors',
          'hover:bg-accent hover:text-accent-foreground text-muted-foreground',
        )}
        aria-label="Change language"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change language"
      >
        <Languages className="h-4 w-4" />
        <span className="text-xs">{SHORT[locale]}</span>
      </button>

      {open && (
        <div role="listbox" className="absolute right-0 top-full mt-1 w-40 rounded-md border border-border bg-[#080a18] shadow-lg z-50">
          {LOCALES.map((option) => (
            <button
              key={option.code}
              role="option"
              aria-selected={locale === option.code}
              lang={option.lang}
              onClick={() => choose(option.code)}
              className={cn(
                'flex items-center justify-between w-full px-3 py-2 text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                locale === option.code && 'bg-accent/50 font-medium',
              )}
            >
              <span>{option.nativeName}</span>
              <span className="text-xs text-muted-foreground">{SHORT[option.code]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
